import { palette } from "lib/miolo";
import formatFinancialValueForChart from "components/company/financials/formatFinancialValueForChart";
import type { TCompanyFinancialsClassification } from "components/fundamentals/classifyFinancialStatements";
import classifyFinancialStatements from "components/fundamentals/classifyFinancialStatements";
import type { FundamentalsLine } from "components/fundamentals/FundamentalsChart";
import FundamentalsChart from "components/fundamentals/FundamentalsChart";
import FundamentalsChartBlankslate from "components/fundamentals/FundamentalsChartBlankslate";
import FundamentalsChartBoxHeader from "components/fundamentals/FundamentalsChartBoxHeader";
import FundamentalsChartContext from "components/fundamentals/FundamentalsChartContext";
import FundamentalsChartContextProvider from "components/fundamentals/FundamentalsChartContextProvider";
import { Fundaments } from "components/fundamentals/Fundaments";
import parse from "date-fns/parse";
import ptBR from "date-fns/locale/pt-BR";
import type { IQuarterlyBrazilianCompanyFinancialsFieldsFragment } from "lib/fragments/__generated__/QuarterlyBrazilianCompanyFinancialsFields.generated";
import type { IYearlyBrazilianCompanyFinancialsFieldsFragment } from "lib/fragments/__generated__/YearlyBrazilianCompanyFinancialsFields.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import type { ReactElement } from "react";
import BankFundamentalsToggles from "./BankFundamentalsToggles";
import CompanyFundamentalsToggles from "./CompanyFundamentalsToggles";
import InsurerFundamentalsToggles from "./InsurerFundamentalsToggles";

type TFinancialStatementRow =
  | IQuarterlyBrazilianCompanyFinancialsFieldsFragment
  | IYearlyBrazilianCompanyFinancialsFieldsFragment;

interface IBrazilianCompanyFundamentalsChartProps {
  financials: TFinancialStatementRow[];
}

export default function BrazilianCompanyFundamentalsChart({
  financials,
}: IBrazilianCompanyFundamentalsChartProps): ReactElement | null {
  if (financials.length === 0) {
    return null;
  }

  const classification = classifyFinancialStatements(financials);

  return (
    <FundamentalsChartContextProvider fundaments={getDefaultSelectedFundaments(classification, financials)}>
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gridColumnGap: 30,
          alignItems: "center",
          margin: "0px auto",
          width: 1140,
        }}
      >
        <div
          css={{
            gridColumn: "span 12",
            boxShadow: "0 4px 6px 0 #33333333",
            backgroundColor: "#ffffff",
            borderRadius: 4,
          }}
        >
          <FundamentalsChartContext.Consumer>
            {({ fundaments }) => (
              <FundamentalsChartBoxHeader title="Gráfico Fundamentalista" grayedOut={fundaments.length === 0} />
            )}
          </FundamentalsChartContext.Consumer>

          <div
            css={{
              padding: "24px 0px",
            }}
          >
            <div
              css={{
                display: "grid",
                gridRowGap: 16,
                padding: "0px 24px 0px 24px",
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridAutoColumns: "max-content",
                  gridAutoFlow: "column",
                  gridColumnGap: 8,
                }}
              >
                <span
                  css={{
                    color: palette.baliHai,
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: "18px",
                    letterSpacing: 0.6,
                  }}
                >
                  Indicadores
                </span>

                <FundamentalsChartContext.Consumer>
                  {({ selectAll, isAllSelected }) => {
                    return isAllSelected ? null : (
                      <button
                        type="button"
                        onClick={selectAll}
                        css={{
                          padding: 0,
                          color: palette.royalBlue,
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: "18px",
                          letterSpacing: 0.6,
                          background: "transparent",
                          border: "none",
                          outline: 0,
                          cursor: "pointer",
                        }}
                      >
                        Mostrar Todos
                      </button>
                    );
                  }}
                </FundamentalsChartContext.Consumer>

                <FundamentalsChartContext.Consumer>
                  {({ deselectAll, fundaments }) => {
                    return fundaments.length === 0 ? null : (
                      <button
                        type="button"
                        onClick={deselectAll}
                        css={{
                          padding: 0,
                          color: palette.link,
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: "18px",
                          letterSpacing: 0.6,
                          background: "transparent",
                          border: "none",
                          outline: 0,
                          cursor: "pointer",
                        }}
                      >
                        Ocultar Todos
                      </button>
                    );
                  }}
                </FundamentalsChartContext.Consumer>
              </div>

              <div
                css={{
                  display: "flex",
                  flexWrap: "wrap",
                  "> button": {
                    marginRight: 8,
                    marginBottom: 8,
                  },
                }}
              >
                {classification.isBank && <BankFundamentalsToggles />}
                {classification.isCompany && <CompanyFundamentalsToggles />}
                {classification.isInsurer && <InsurerFundamentalsToggles />}
              </div>
            </div>

            <FundamentalsChartContext.Consumer>
              {({ fundaments }) => {
                if (fundaments.length === 0) {
                  return (
                    <div className="flex flex-col items-center space-y-4 text-center">
                      <div className="flex rounded-full bg-gray-50 p-12">
                        <FundamentalsChartBlankslate width={120} height={120} />
                      </div>

                      <p className="font-aeonik font-normal text-gray-600">
                        Selecione pelo menos um indicador para visualização em gráfico.
                      </p>
                    </div>
                  );
                }

                return (
                  <FundamentalsChart<TFinancialStatementRow[]>
                    chart={{
                      width: 1140,
                      height: 480,
                      margin: {
                        top: 24,
                        right: fundaments.length === 2 ? 56 : 32,
                        bottom: 24,
                        left: 56,
                      },
                      padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                      },
                    }}
                    lines={getChartLines(fundaments)}
                    getFiscalDate={({ endOfPeriod }) => {
                      return parse(endOfPeriod, "yyyy-MM-dd", new Date(), {
                        locale: ptBR,
                      });
                    }}
                    yAxes={fundaments.length === 2 ? "left-and-right" : "left-only"}
                    data={financials}
                    axisBottomDateFormat={classification.isQuarterly ? "MM/yy" : "yyyy"}
                  />
                );
              }}
            </FundamentalsChartContext.Consumer>
          </div>
        </div>
      </div>
    </FundamentalsChartContextProvider>
  );
}

function getDefaultSelectedFundaments(
  classification: TCompanyFinancialsClassification,
  financials: TFinancialStatementRow[],
): Fundaments[] {
  if (classification.isBank) {
    return [
      Fundaments.NET_EQUITY,
      Fundaments.FINANCIAL_INTERMEDIATION_REVENUE,
      Fundaments.NET_INCOME,
      Fundaments.PROVISION_FOR_CREDIT_LOSSES,
      Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      Fundaments.NET_MARGIN,
      classification.isYearly ? Fundaments.RETURN_ON_EQUITY : null,
      Fundaments.PAYOUT,
    ].filter(notUndefinedOrNull);
  }

  if (classification.isCompany) {
    return [
      Fundaments.NET_EQUITY,
      Fundaments.NET_REVENUE,
      Fundaments.EBITDA,
      Fundaments.DEPRECIATION_AND_AMORTIZATION,
      Fundaments.CAPEX,
      Fundaments.NET_INCOME,
      Fundaments.CASH,
      Fundaments.DEBT,
      Fundaments.NET_DEBT,
      Fundaments.CASH_FLOW_FROM_OPERATIONS,
      Fundaments.FREE_CASH_FLOW,
      Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      Fundaments.NET_MARGIN,
      classification.isYearly ? Fundaments.RETURN_ON_EQUITY : null,
      Fundaments.PAYOUT,
    ].filter(notUndefinedOrNull);
  }

  if (classification.isInsurer) {
    const hasDebt = financials.some((financialStatementRow) => {
      if (
        financialStatementRow.__typename === "YearlyBrazilianInsurerFinancialStatementRow" ||
        financialStatementRow.__typename === "QuarterlyBrazilianInsurerFinancialStatementRow"
      ) {
        return financialStatementRow.debt !== 0;
      }

      return false;
    });

    return [
      Fundaments.NET_EQUITY,
      Fundaments.NET_REVENUE,
      Fundaments.EBITDA,
      Fundaments.DEPRECIATION_AND_AMORTIZATION,
      Fundaments.CAPEX,
      Fundaments.NET_INCOME,
      Fundaments.CASH,
      hasDebt ? Fundaments.DEBT : null,
      hasDebt ? Fundaments.NET_DEBT : null,
      Fundaments.CASH_FLOW_FROM_OPERATIONS,
      Fundaments.FREE_CASH_FLOW,
      Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      Fundaments.NET_MARGIN,
      classification.isYearly ? Fundaments.RETURN_ON_EQUITY : null,
      Fundaments.PAYOUT,
    ].filter(notUndefinedOrNull);
  }

  return [];
}

function getChartLines(fundaments: Fundaments[]): FundamentalsLine<TFinancialStatementRow>[] {
  const lines: FundamentalsLine<TFinancialStatementRow>[] = [
    {
      key: Fundaments.NET_EQUITY,
      label: "Pat. Líq",
      value: (datum) => datum.netEquity,
      stroke: palette.kumera,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.FINANCIAL_INTERMEDIATION_REVENUE,
      label: "Receita Líq.",
      value: (datum) => {
        if (
          datum.__typename === "YearlyBrazilianBankFinancialStatementRow" ||
          datum.__typename === "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.financialIntermediationRevenue;
        }

        return datum.netRevenue;
      },
      stroke: palette.fruitSalad,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.NET_REVENUE,
      label: "Receita Líq.",
      value: (datum) => {
        if (
          datum.__typename === "YearlyBrazilianBankFinancialStatementRow" ||
          datum.__typename === "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.financialIntermediationRevenue;
        }

        return datum.netRevenue;
      },
      stroke: palette.fruitSalad,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.EBITDA,
      label: "EBITDA",
      value: (datum) => {
        if (
          datum.__typename !== "YearlyBrazilianBankFinancialStatementRow" &&
          datum.__typename !== "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.ebitda;
        }

        return 0;
      },
      stroke: palette.seaGreen,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.DEPRECIATION_AND_AMORTIZATION,
      label: "D&A",
      value: (datum) => {
        if (
          datum.__typename !== "YearlyBrazilianBankFinancialStatementRow" &&
          datum.__typename !== "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.depreciationAndAmortization;
        }

        return 0;
      },
      stroke: palette.zuccini,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.CAPEX,
      label: "CAPEX",
      value: (datum) => {
        if (
          datum.__typename !== "YearlyBrazilianBankFinancialStatementRow" &&
          datum.__typename !== "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.capex;
        }

        return 0;
      },
      stroke: palette.chathamsBlue,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.NET_INCOME,
      label: "Lucro Líq.",
      value: (datum) => datum.netIncome,
      stroke: palette.oceanGreen,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.NET_MARGIN,
      stroke: "#507712",
      label: "Mrg. Líq.",
      value: (datum) => (datum.netIncome < 0 ? 0 : Math.max(datum.netMargin, 0)),
      valueFormatter: (netMargin, datum) => {
        if (datum.netMargin < 0 || datum.netIncome < 0) {
          return "P";
        }

        const formattedNetMargin = netMargin.toFixed(netMargin < 1 && netMargin !== 0 ? 1 : 0).replace(".", ",");
        return `${formattedNetMargin}%`;
      },
    },
    {
      key: Fundaments.RETURN_ON_EQUITY,
      stroke: palette.stromboli,
      label: "ROE",
      value: (datum) => datum.returnOnEquity,
      valueFormatter: (returnOnEquity, datum) => {
        if (datum.returnOnEquity < 0 || datum.netIncome < 0 || datum.netEquity < 0) {
          return "P";
        }

        const formattedReturnOnEquity = returnOnEquity.toFixed(returnOnEquity < 1 ? 1 : 0).replace(".", ",");
        return `${formattedReturnOnEquity}%`;
      },
    },
    {
      key: Fundaments.PROVISION_FOR_CREDIT_LOSSES,
      label: "PDD",
      value: (datum) => {
        if (
          datum.__typename === "YearlyBrazilianBankFinancialStatementRow" ||
          datum.__typename === "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.provisionForCreditLosses;
        }

        return 0;
      },
      stroke: palette.mojo,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.CASH,
      label: "Caixa",
      value: (datum) => {
        if (
          datum.__typename !== "YearlyBrazilianBankFinancialStatementRow" &&
          datum.__typename !== "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.cash;
        }

        return 0;
      },
      stroke: palette.dixie,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.DEBT,
      label: "Div. Bruta",
      value: (datum) => {
        if (
          datum.__typename !== "YearlyBrazilianBankFinancialStatementRow" &&
          datum.__typename !== "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.debt;
        }

        return 0;
      },
      stroke: palette.burntUmber,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.NET_DEBT,
      label: "Div. Líq.",
      value: (datum) => {
        if (
          datum.__typename !== "YearlyBrazilianBankFinancialStatementRow" &&
          datum.__typename !== "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.netDebt;
        }

        return 0;
      },
      stroke: palette.mojo,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.CASH_FLOW_FROM_OPERATIONS,
      label: "FCO",
      value: (datum) => {
        if (
          datum.__typename !== "YearlyBrazilianBankFinancialStatementRow" &&
          datum.__typename !== "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.cashFlowFromOperations;
        }

        return 0;
      },
      stroke: palette.tundora,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.FREE_CASH_FLOW,
      label: "FCL CAPEX",
      value: (datum) => {
        if (
          datum.__typename !== "YearlyBrazilianBankFinancialStatementRow" &&
          datum.__typename !== "QuarterlyBrazilianBankFinancialStatementRow"
        ) {
          return datum.freeCashFlow;
        }

        return 0;
      },
      stroke: palette.scienceBlue,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      label: "Proventos",
      value: (datum) => datum.payout || datum.cashFlowForDividends,
      stroke: palette.luxorGold,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.PAYOUT,
      stroke: "#CB6E17",
      label: "Payout",
      value: (datum) => datum.cashFlowForDividendsToNetIncomeRatio || datum.payoutToNetIncomeRatio,
      valueFormatter: (payout, datum) => (datum.netIncome < 0 ? "P" : `${payout}%`),
    },
  ];

  return lines.filter((line) => {
    if (line.key) {
      return fundaments.includes(line.key as Fundaments);
    }

    return false;
  });
}
